import {
  getBikeFitment,
  getFittingBikes,
} from 'app/product/utils/getBikeFitment';
import { CheckIcon, InfoIcon } from 'lucide-react';
import type { ProductDocument, ProductLight } from 'models/product';
import type { UserDocument } from 'models/user';
import { Tooltip } from 'react-tooltip';

type ProductFitmentProps = {
  product: ProductLight | ProductDocument;
  userDoc: UserDocument | null | undefined;
};

export default function ProductFitment({ product, userDoc }: ProductFitmentProps) {
  // Fitment tags (universal, fits, vehicle-specific) only make sense for parts;
  // vehicles, gear and collectibles never carry vehicle fitment.
  const isPart = product.category?.toLowerCase().includes('part') ?? false;
  if (!isPart) return null;

  const isUniversal =
    (product as ProductDocument)?.fitting_bikes?.universal ?? false;
  const isWarehouse = product.integration_info?.type === 'warehouse';

  const fittingBikes = getFittingBikes(product);

  const fitment = userDoc?.bikes?.length
    ? getBikeFitment(fittingBikes, userDoc.bikes, false)
    : null;

  const status = isUniversal
    ? 'universal'
    : fitment?.status === 'fits'
      ? 'fits'
      : isWarehouse && fittingBikes.length > 0
        ? 'vehicle-specific'
        : null;

  if (!status) return null;

  const matchedBikeName = fitment?.bike
    ? [fitment.bike.year, fitment.bike.make, fitment.bike.model]
        .filter(Boolean)
        .join(' ')
    : '';

  const tooltipId = `fitment-tooltip-${product.id}`;

  const label =
    status === 'fits'
      ? 'Fits vehicle'
      : status === 'universal'
        ? 'Universal'
        : 'Vehicle specific';

  const textColor = status === 'fits' ? 'text-[#1E7A1E]' : 'text-yellow-600';

  const tooltipMessage =
    status === 'fits'
      ? matchedBikeName
        ? `This part fits your ${matchedBikeName}.`
        : 'This part fits a vehicle in your garage.'
      : status === 'universal'
        ? 'This part is listed as universal fitment.'
        : 'Select your vehicle to ensure this part fits your exact year, make, and model.';

  return (
    <div
      className="mb-4 flex w-fit items-center gap-2 font-medium hover:cursor-help"
      data-tooltip-id={tooltipId}
      data-tooltip-content={tooltipMessage}
    >
      {status === 'fits' ? (
        <div className="h-6 w-6 rounded-full bg-[#1E7A1E] p-1">
          <CheckIcon className="h-full w-full text-white" />
        </div>
      ) : (
        <div className="h-6 w-6">
          <InfoIcon className="h-full w-full text-yellow-600" />
        </div>
      )}

      <span className={textColor}>{label}</span>

      <Tooltip id={tooltipId} place="top" className="z-[9999] max-w-[28rem]" />
    </div>
  );
}
